import { Alert, Button, Checkbox, Col, Form, FormInstance, Input, Modal, Row, Upload, UploadProps, notification, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

import Bank from 'pages/ThirdParty/ThirdPartySFS/ThirdPartyCreation/components/Bank';
import ButtonLink from 'components/theme_components/ButtonLink';
import { Color } from 'antd/es/color-picker';
import FormInput from 'components/FormInput';
import ParListSelect from 'components/ParListSelect';
import StaticCard from 'components/theme_components/StatCard';
import { UploadDocumentFile } from 'utils/request';
import { ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useGetProductList } from 'pages/PosPhonesShop/MyParValuesSelect';
import { isValidIBAN, extractIBAN, electronicFormatIBAN, validateIBAN } from 'ibantools';
import { getAvailablePriorities } from 'services/thirdParty';
import { getParbankByBkcode } from 'services/parametersService';
import { debounce } from 'lodash';


export type TabBanKAccountProps = {
  form: FormInstance;
  refetch: () => void;
  setVisible: (visible: boolean) => void;
  tpid?: number;
  action: string;
  record?: any;
  onDone: () => void;
};

// Step 1: Define the props interface for DynamicPrioritySelect
interface DynamicPrioritySelectProps {
  tpid?: number;
  prcode: string;
  excludedTpbkid?: number;
  value?: string | number;
  onChange?: (value: string | number) => void;
  className?: string;
}

const TabBanKAccount = (props: TabBanKAccountProps) => {
  const {
    action,
    record,
    tpid,
    form,
    setVisible,
    refetch,
    onDone
  } = props;

  // Log the value of tpid to check if it is undefined
  console.log('tpid used :', tpid);

  const uploadProps: UploadProps = {
    action: tpid ? `http://localhost:3001/thirdparty-sfs/${tpid}/CONC` : '',
    onChange({ file, fileList }: { file: any; fileList: any }) {
      if (file.status !== 'uploading') {
        console.log(file, fileList);
      }
    }
  };

  const { data: products } = useGetProductList();
  const [loadingBic, setLoadingBic] = useState(false);

const handleBicLookup = debounce(async (iban: string) => {
  // Clear fields if IBAN is empty
  if (!iban ) {
    form?.setFieldsValue({
      tpbic: '',
      
    });
    return;
  }

  try {
    setLoadingBic(true);
    const ibanData = extractIBAN(iban);
    let bankIdentifier = ibanData.bankIdentifier;

    // Fallback for French IBANs
    if (!bankIdentifier && iban.startsWith('FR') && iban.length >= 14) {
      bankIdentifier = iban.substring(4, 9); // Positions 4-8 in French IBANs
    }

    if (bankIdentifier) {
      const response = await getParbankByBkcode({ 
        bkcode: bankIdentifier 
      });
      
      if (response?.data?.bkbic) {
        form?.setFieldsValue({
          tpbic: response.data.bkbic,
          //tpbankname: response.data.bkname || ''
        });
      } else {
        // Clear if no BIC found
        form?.setFieldsValue({
          tpbic: '',
         
        });
      }
    } else {
      // Clear if no bank identifier could be extracted
      form?.setFieldsValue({
        tpbic: '',
        tpbankname: ''
      });
    }
  } catch (error) {
    console.error('BIC lookup failed:', error);
    form?.setFieldsValue({
      tpbic: '',
      tpbankname: ''
    });
  } finally {
    setLoadingBic(false);
  }
}, 500);

  // Step 2: Create the DynamicPrioritySelect component with proper typing
  const DynamicPrioritySelect = ({ 
    tpid, 
    prcode, 
    excludedTpbkid, 
    value, 
    onChange, 
    className 
  }: DynamicPrioritySelectProps) => {
    const [availablePriorities, setAvailablePriorities] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    // Step 3: Fetch available priorities when component mounts or props change  
    useEffect(() => {
      const fetchAvailablePriorities = async () => {
        if (tpid === undefined || !prcode) {
          console.log('Skipping API call - tpid or prcode missing:', { tpid, prcode });
          setAvailablePriorities([1, 2, 3, 4, 5, 6]);
          return;
        }
        
        setLoading(true);
        
        try {
          const response = await getAvailablePriorities({
            tpid,
            prcode,
            excludedTpbkid
          });

          if (response.code === '200' && response.data) {
            console.log('SUCCESS: API returned priorities:', response.data);
            setAvailablePriorities(response.data);
          } else {
            console.log('API failed with response:', response);
            setAvailablePriorities([1, 2, 3, 4, 5, 6]);
          }
        } catch (error) {
          console.error('Error fetching available priorities:', error);
          setAvailablePriorities([1, 2, 3, 4, 5, 6]);
        } finally {
          setLoading(false);
        }
      };

      fetchAvailablePriorities();
    }, [tpid, prcode, excludedTpbkid]);

    // Step 4: Return the Select component with available priorities
    return (
      <Select
        className={className}
        value={value}
        onChange={onChange}
        loading={loading}
        placeholder="Sélectionner une priorité"
        style={{ width: '100%' }}
      >
        {availablePriorities.map((priority) => (
          <Select.Option key={priority} value={priority.toString()}>
            {priority}
          </Select.Option>
        ))}
      </Select>
    );
  };
  
  useEffect(() => {
    if (action === 'create') {
      form.resetFields();
      form.setFieldsValue({
        bktype: 'INOUT',
        currcode: 'EUR',
        countcode: 'FR',
        tpcbtype: '',
        bkstatus: 'INIT',
        tpid: tpid,
        bkstartdate: dayjs(),
        bkstatusdate: dayjs()
      });
      console.log('tpid : ', form.getFieldValue('tpid'));
    } else if (record) {
      // Transform the product list into the correct format for the form
      const formValues = {
        ...record,
        bkholder: record.bkholder ?? '',
        bkstartdate: record.bkstartdate ? dayjs(record.bkstartdate) : null,
        bkenddate: record.bkenddate ? dayjs(record.bkenddate) : null,
        bkstatusdate: record.bkstatusdate ? dayjs(record.bkstatusdate) : null,
      };

      if (record.tpBankAccountProductList) {
        record.tpBankAccountProductList.forEach((product: any) => {
          // Set the checkbox value
          formValues[product.prcode] = true;
          // Set the priority value
          if (!formValues.priority) formValues.priority = {};
          formValues.priority[product.prcode] = product.priority.toString();
        });
      }

      form.setFieldsValue(formValues);
    }

  }, [action, record, form, tpid]);

  const [countryCode, setCountryCode] = useState<string>(form.getFieldValue('countcode') || 'FR');
  const [ibanRest, setIbanRest] = useState<string>('');

  useEffect(() => {
    const fullIban = countryCode + ibanRest;
    const ibanDetails = extractIBAN(fullIban);
    form?.setFieldsValue({
      // [ibanField]: fullIban,
      tpiban: fullIban,
      bkcode:ibanDetails.bankIdentifier || '',
    });5
  }, [countryCode, ibanRest, form]);

  const ibanValidator = (iban: string): boolean => {
    if (iban.length < 27) return false;
    if (!/^[A-Z]{2}/.test(iban)) return false;
    // if (!/^[A-Z]{2}[A-Z0-9]$/.test(iban)) return false
    return true;
  };
  
  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value?.toUpperCase().replace(/\s/g, '');
    setIbanRest(inputValue);
    if (!inputValue || inputValue.length < 2) {
    form?.setFieldsValue({
      tpbic: '',
      tpbankname: '',
      bkcode: '',
      bkbranchcode: '',
      bkaccountnum: '',
      bkaccountkey: ''
    });
    return;
  }
    const fullIban = countryCode + inputValue;
    const ibanDetails = extractIBAN(fullIban);

    form?.setFieldsValue({
      tpiban: fullIban,
      bkcode:ibanDetails.bankIdentifier || '',
      bkbranchcode: fullIban.slice(9, 14),
      bkaccountnum: fullIban.slice(14, 25),
      bkaccountkey: fullIban.slice(25, 27),
    });
   if (ibanDetails.bankIdentifier && ibanDetails.bankIdentifier.length >= 2) {
  handleBicLookup(fullIban);
}

  };

  const handleSubmit = async () => {
    try {
      if (action !== 'create') return; 

      const values = await form.validateFields();
      console.log('values.....', values);

      const tpBankAccountProductList = products
        ?.filter((product) => values[product.prcode])
        .map((product) => ({
          tpid: tpid,
          tpbkid: record?.tpbkid,
          prcode: product.prcode,
          priority: parseInt(values.priority?.[product.prcode] || '1', 10)
        }));

      const { priority, ...restValues } = values;
      products?.forEach((product) => {
        delete restValues[product.prcode];
      });

      const iban = values.tpiban;
      const countryCode = iban?.substring(0, 2);
      const controlKey = iban?.substring(2, 4);
      const rib = iban?.substring(4);

      const bankCode = rib?.substring(0, 5);
      const branchCode = rib?.substring(5, 10);
      const accountNumber = rib?.substring(10, 21);
      const ribKey = rib?.substring(21);

      const payload = {
        ...values,
        tpid: tpid,
        tpBankAccountProductList: tpBankAccountProductList || [],
        bktype: values.bktype || 'INOUT',
        currcode: values.currcode || 'EUR',
        countcode: values.countcode || 'FR',
        tpcbtype: values.tpcbtype || '',
        bkstatus: values.bkstatus || 'ACTIVE'
      };

      console.log('payload.....', payload);
      // onSave(payload, action);
      setVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
      
    }
  };

   useEffect(() => {
    return () => {
      handleBicLookup.cancel();
    };
  }, []);
  return (
    <>
      <Form layout="vertical" form={form} className="compact-form">
        <br />
        <StaticCard
          title=""
          value="Il est recommandé que l'ajout d'un compte soit fait par le concessionnaire."
          variant="notif"
          className="mb-8 staticNotf"
          valueStyle={{ backgroundColor: "#F7C0A929" }}
        />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '10px',boxShadow: 'none ',  }} className="custom-button-container">
          <Upload
            {...uploadProps}
            accept=".pdf,.jpg,.png"
            showUploadList={false}
            beforeUpload={() => {
              const container = document.querySelector('.custom-button-container');
              const loader = document.createElement('div');
              loader.className = 'loader';
              loader.style.width = '0%';
              loader.style.height = '5px';
              loader.style.backgroundColor = 'orange';
              loader.style.transition = 'width 0.5s';
              container?.appendChild(loader);
              const button = container?.querySelector('button');
              if (button) {
                button.style.display = 'none';
              }
              return true;
            }}
            onChange={(info: any) => {
              const loader = document.querySelector('.loader') as HTMLElement;
              if (info.file.status === 'uploading') {
                loader.style.width = '50%';
              } else if (info.file.status === 'done') {
                loader.style.width = '100%';
                setTimeout(() => {
                  loader.remove();
                  notification.success({
                    message: 'Upload successful',
                    description: `${info.file.name} file uploaded successfully.`,
                  });
                  const fileUrl = URL.createObjectURL(info.file.originFileObj);
                  const fileLink = document.createElement('a');
                  fileLink.href = fileUrl;
                  fileLink.target = '_blank';
                  fileLink.innerText = info.file.name;
                  const container = document.querySelector('.custom-button-container');
                  container?.appendChild(fileLink);
                }, 500);
              } else if (info.file.status === 'error') {
                loader.remove();
                notification.error({
                  message: 'Upload failed',
                  description: `${info.file.name} file upload failed.`,
                });
                const button = document.querySelector('.custom-button-container button') as HTMLElement;
                if (button) {
                  button.style.display = 'block';
                }
              }
            }}
          >
            <Button
              htmlType='button'
              icon={<UploadOutlined style={{ color: '#DD7248' }} />}
              style={{
                marginBottom: 15,
                fontWeight: '500 ',
                border: 'none',
                fontFamily: 'Encode Sans Semi Expanded ',
                fontSize: '15px',
                lineHeight: '20px',
                letterSpacing: '0.1px',
                boxShadow: 'none ', 
                transition: 'color 0.3s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#FF5733')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'inherit')}
            >
              <span>
                Ajouter un RIB<span style={{ color: 'red' }}> *</span>
              </span>
            </Button>
          </Upload>
        </div>

        <Row className="no-border-radius" gutter={[12, 12]} style={{ marginBottom: 15 }}>
          {action !== 'edit' && (
            <FormInput
              col
              md={24}
              name="countcode"
              rules={[{ required: true }]}
              nolabel
              initialValue={countryCode}
              hidden={action === 'edit'}
              style={{ marginBottom: 10 }}
              noSpecialChars
            >
              <ParListSelect
                parcode="COUNTRY"
                className="ut-input-modal no-border-radius"
                placeholder="Pays*"
                style={{ height: 33, borderRadius: 0 }}
                onChange={(value) => {
                  const newCountryCode = value.toUpperCase();
                  setCountryCode(newCountryCode);
                  const currentIban = form?.getFieldValue('tpiban') || '';
                  form?.setFieldsValue({
                    tpiban: newCountryCode + currentIban.slice(2),
                  });
                }}
              />
            </FormInput>
          )}

          <Col span={24}>
            <div style={{ color: 'gray', marginBottom: '8px' }}>
              Utilisation<span style={{ color: 'red' }}>*</span>
              <span style={{ marginLeft: 5, cursor: 'pointer' }} title="Information">
                <i className="fas fa-info-circle" />
              </span>
            </div>
            <Row className="no-border-radius" gutter={[12, 12]} style={{ marginBottom: 15 }}>
              {products?.map((product) => (
                <React.Fragment key={product.prcode}>
                  <Col span={12}>
                    <Row gutter={25}>
                      <Col span={6}>
                        <Form.Item name={product.prcode} valuePropName="checked">
                          <Checkbox>{product.prcode}</Checkbox>
                        </Form.Item>
                      </Col>
                      <Col span={18}>
                        <Form.Item shouldUpdate noStyle>
                          {() =>
                            form.getFieldValue(product.prcode) && (
                              <Form.Item name={["priority", product.prcode]} noStyle>
                                <DynamicPrioritySelect 
                                  className="ut-input-modal"
                                  tpid={tpid}
                                  prcode={product.prcode}
                                  excludedTpbkid={action === 'edit' ? record?.tpbkid : undefined}
                                />
                              </Form.Item>
                            )
                          }
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </React.Fragment>
              ))}
            </Row>
          </Col>

          <FormInput
            col
            md={24}
            name="tpbankname"
            nolabel
            style={{ marginBottom: 10 }}
            noSpecialChars
          >
            <Input
              className="ut-input-modal"
              placeholder="Libelle du compte*"
              style={{ height: 33, borderRadius: 0 }}
            />
          </FormInput>

          {action !== 'edit' && (
            <FormInput
              col
              md={24}
              name="bkholder"
              rules={[{ required: true }]}
              hidden={action === 'edit'}
              nolabel
              style={{ marginBottom: 10 }}
              noSpecialChars
            >
              <Input
                className="ut-input-modal"
                placeholder="Titulaire*"
                style={{ height: 33, borderRadius: 0 }}
              />
            </FormInput>
          )}

          {action !== 'edit' && (
            <FormInput
              col
              md={24}
              name="tpiban"
              hidden={action === 'edit'}
              nolabel
              noSpecialChars
              style={{ marginBottom: 10 }}
              rules={[
                { required: true, message: "L'IBAN est requis" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) return Promise.resolve();
                    const fullIban = countryCode + ibanRest;
                    const checkCode = fullIban.slice(2, 4);
                    if (!/^\d{2}$/.test(checkCode)) {
                      return Promise.reject(new Error("Les deux caractères après le code pays doivent être numériques"));
                    }
                    if (!isValidIBAN(fullIban)) {
                                     
                    return Promise.reject(new Error('IBAN invalid'));
                                    }
                    if (!ibanValidator(fullIban)) {
                      return Promise.reject(new Error("IBAN invalide"));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <div className="d-flex align-center">
                <Input
                  disabled
                  className="ut-input-modal no-border-radius"
                  value={countryCode}
                  style={{ height: 33, width: 50 }}
                  defaultValue={form.getFieldValue('countcode')}
                />
                <Input
                  className="ut-input-modal flex-1"
                  placeholder="IBAN*"
                  value={ibanRest}
                  onChange={handleIBANChange}
                  style={{ height: 33, borderRadius: 0 }}
                />
              </div>
            </FormInput>
          )}

          {action !== 'edit' && (
            <FormInput
              col
              md={24}
              name="tpbic"
              nolabel
              hidden={action === 'edit'}
              style={{ marginBottom: 10 }}
              rules={[{ required: true }]}
              noSpecialChars
            >
              <Input
                className="ut-input-modal"
                placeholder="BIC*"
                suffix={
      <span>
        {loadingBic ? <Spin size="small" /> : null}
           </span>
    }
              />
            </FormInput>
          )}

          <Form.Item name="bkcode" hidden />
          <Form.Item name="bkbranchcode" hidden />
          <Form.Item name="bkaccountnum" hidden />
          <Form.Item name="bkaccountkey" hidden />
        </Row>
      </Form>
    </>
  );
};

export default TabBanKAccount;